import React, { useState, useEffect } from 'react';
import DashboardService from '../services/dashboardService';
import Icons from './Icons';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function isExpired(expiresAt) {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
}

const priorityOptions = [
  { value: 'normal', label: 'Normal', desc: 'Standard update', color: 'border-primary-300 bg-primary-50 text-primary-800' },
  { value: 'important', label: 'Important', desc: 'Highlighted visibility', color: 'border-amber-300 bg-amber-50 text-amber-800' },
  { value: 'urgent', label: 'Urgent', desc: 'Top priority', color: 'border-red-300 bg-red-50 text-red-800' },
];

const priorityBadge = (p) => {
  const s = {
    urgent: 'bg-red-100 text-red-700',
    important: 'bg-amber-100 text-amber-700',
    normal: 'bg-gray-100 text-gray-600',
  };
  return <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${s[p] || s.normal}`}>{p}</span>;
};

const AnnouncementModal = ({ announcement, onSave, onClose, saving }) => {
  const isEditing = !!announcement;
  const [title, setTitle] = useState(announcement?.title || '');
  const [content, setContent] = useState(announcement?.content || '');
  const [priority, setPriority] = useState(announcement?.priority || 'normal');
  const [isPinned, setIsPinned] = useState(announcement?.is_pinned || false);
  const [hasExpiration, setHasExpiration] = useState(!!announcement?.expires_at);
  const [expiresAt, setExpiresAt] = useState(announcement?.expires_at ? announcement.expires_at.slice(0, 10) : '');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = 'Title is required';
    if (!content.trim()) e.content = 'Content is required';
    if (title.length > 120) e.title = 'Title must be under 120 characters';
    if (hasExpiration && !expiresAt) e.expiresAt = 'Select an expiration date';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSave({
      title: title.trim(),
      content: content.trim(),
      priority,
      is_pinned: isPinned,
      expires_at: hasExpiration && expiresAt ? new Date(expiresAt + 'T23:59:00Z').toISOString() : null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            {Icons.megaphone('w-5 h-5 text-gray-400')}
            <h2 className="text-lg font-semibold text-gray-900">{isEditing ? 'Edit Announcement' : 'New Announcement'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">{Icons.close('w-5 h-5')}</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Board meeting rescheduled"
              maxLength={120}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.title ? <p className="text-xs text-red-500">{errors.title}</p> : <span />}
              <p className="text-xs text-gray-400">{title.length}/120</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your announcement here..."
              rows={4}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${errors.content ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
            />
            {errors.content && <p className="text-xs text-red-500 mt-1">{errors.content}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <div className="grid grid-cols-3 gap-2">
              {priorityOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setPriority(opt.value)}
                  className={`relative border-2 rounded-lg px-3 py-3 text-left transition-all ${priority === opt.value ? opt.color + ' ring-1 ring-offset-1 ring-current' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                >
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="text-xs opacity-70 mt-0.5">{opt.desc}</p>
                  {priority === opt.value && <div className="absolute top-2 right-2">{Icons.check('w-3.5 h-3.5')}</div>}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center space-x-2.5 cursor-pointer">
              <div className={`w-9 h-5 rounded-full relative transition-colors ${isPinned ? 'bg-primary-600' : 'bg-gray-200'}`} onClick={() => setIsPinned(!isPinned)}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isPinned ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Pin to top</span>
                <p className="text-xs text-gray-400">Always visible at top of feed</p>
              </div>
            </label>

            <div>
              <label className="flex items-center space-x-2.5 cursor-pointer mb-2">
                <div className={`w-9 h-5 rounded-full relative transition-colors ${hasExpiration ? 'bg-primary-600' : 'bg-gray-200'}`} onClick={() => setHasExpiration(!hasExpiration)}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${hasExpiration ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Set expiration</span>
                  <p className="text-xs text-gray-400">Auto-hide after a certain date</p>
                </div>
              </label>
              {hasExpiration && (
                <div className="ml-12">
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.expiresAt ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.expiresAt && <p className="text-xs text-red-500 mt-1">{errors.expiresAt}</p>}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">Preview</label>
            <div className={`border rounded-lg p-4 ${priority === 'urgent' ? 'bg-red-50 border-red-200 text-red-900' : priority === 'important' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-primary-50 border-primary-200 text-primary-900'}`}>
              <div className="flex items-start space-x-3">
                <span className="mt-0.5">{priority === 'urgent' ? Icons.alertTriangle('w-4 h-4 text-red-500') : priority === 'important' ? Icons.bolt('w-4 h-4 text-amber-500') : Icons.info('w-4 h-4 text-primary-500')}</span>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-semibold text-sm">{title || 'Announcement title'}</h4>
                    {isPinned && <span className="text-xs opacity-50 flex items-center space-x-0.5">{Icons.pin('w-3 h-3')} <span>Pinned</span></span>}
                  </div>
                  <p className="text-sm opacity-80">{content || 'Your message will appear here...'}</p>
                  <p className="text-xs opacity-50 mt-1.5">just now</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end space-x-3 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Publish Announcement'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteModal = ({ announcement, onConfirm, onClose, deleting }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">{Icons.trash('w-5 h-5 text-red-500')}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Announcement</h3>
        <p className="text-sm text-gray-500 mb-1">Are you sure you want to delete <strong>&quot;{announcement.title}&quot;</strong>?</p>
        <p className="text-xs text-gray-400">This will remove it for all organization members.</p>
      </div>
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end space-x-3 bg-gray-50 rounded-b-xl">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
        <button
          onClick={() => onConfirm(announcement.id)}
          disabled={deleting}
          className="px-5 py-2 text-sm font-medium bg-danger-600 text-white rounded-lg hover:bg-danger-700 shadow-sm disabled:opacity-50"
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

const AdminAnnouncementsManager = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      setLoading(true);
      const result = await DashboardService.getAllAnnouncementsAdmin();
      setAnnouncements(result.announcements || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingAnnouncement) {
        await DashboardService.updateAnnouncement(editingAnnouncement.id, data);
        showSuccess('Announcement updated');
      } else {
        await DashboardService.createAnnouncement(data);
        showSuccess('Announcement published');
      }
      setShowModal(false);
      setEditingAnnouncement(null);
      await loadAnnouncements();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      await DashboardService.deleteAnnouncement(id);
      setDeletingAnnouncement(null);
      showSuccess('Announcement deleted');
      await loadAnnouncements();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePin = async (announcement) => {
    try {
      await DashboardService.updateAnnouncement(announcement.id, { is_pinned: !announcement.is_pinned });
      await loadAnnouncements();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await DashboardService.updateAnnouncement(announcement.id, { is_active: !announcement.is_active });
      await loadAnnouncements();
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredAnnouncements = announcements
    .filter((a) => {
      if (filter === 'active') return a.is_active && !isExpired(a.expires_at);
      if (filter === 'expired') return !a.is_active || isExpired(a.expires_at);
      return true;
    })
    .filter((a) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const activeCount = announcements.filter((a) => a.is_active && !isExpired(a.expires_at)).length;
  const expiredCount = announcements.filter((a) => !a.is_active || isExpired(a.expires_at)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center space-x-2.5 mb-1">
            {Icons.megaphone('w-6 h-6 text-gray-400')}
            <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          </div>
          <p className="text-sm text-gray-500">Create and manage announcements for your organization</p>
        </div>
        <button
          onClick={() => { setEditingAnnouncement(null); setShowModal(true); }}
          className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
        >
          {Icons.plus('w-4 h-4')}<span>New Announcement</span>
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center space-x-2 text-red-800 text-sm">
          {Icons.alertTriangle('w-4 h-4 text-red-500')}<span className="font-medium">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center space-x-2 text-green-800 text-sm">
          {Icons.check('w-4 h-4 text-green-500')}<span className="font-medium">{successMessage}</span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-lg p-1">
          {[
            { id: 'active', label: 'Active', count: activeCount },
            { id: 'all', label: 'All', count: announcements.length },
            { id: 'expired', label: 'Inactive', count: expiredCount },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3.5 py-1.5 text-sm rounded-md font-medium transition-colors ${filter === tab.id ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
            >
              {tab.label}<span className={`ml-1.5 text-xs ${filter === tab.id ? 'text-primary-100' : 'text-gray-400'}`}>{tab.count}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{Icons.search('w-4 h-4')}</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search announcements..."
            className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
          />
        </div>
      </div>

      <div className="space-y-3">
        {filteredAnnouncements.length > 0 ? (
          filteredAnnouncements.map((a) => {
            const expired = isExpired(a.expires_at);
            const inactive = !a.is_active || expired;
            const totalRecipients = parseInt(a.total_recipients) || 0;
            const readCount = parseInt(a.read_count) || 0;
            const readPct = totalRecipients > 0 ? Math.round((readCount / totalRecipients) * 100) : 0;
            return (
              <div key={a.id} className={`bg-white rounded-lg border shadow-sm transition-all hover:shadow-md ${inactive ? 'border-gray-200 opacity-60' : a.is_pinned ? 'border-primary-200' : 'border-gray-200'}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2 flex-wrap gap-y-1">
                        <h3 className="font-semibold text-gray-900">{a.title}</h3>
                        {priorityBadge(a.priority)}
                        {a.is_pinned && <span className="flex items-center space-x-0.5 text-primary-500 text-xs font-medium">{Icons.pinFilled('w-3.5 h-3.5')} <span>Pinned</span></span>}
                        {inactive && <span className="flex items-center space-x-0.5 text-gray-400 text-xs font-medium">{Icons.eyeOff('w-3.5 h-3.5')} <span>{expired ? 'Expired' : 'Hidden'}</span></span>}
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">{a.content}</p>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span>By {a.created_by_first_name} {a.created_by_last_name}</span>
                        <span className="flex items-center space-x-1">{Icons.clock('w-3.5 h-3.5')}<span>{formatTimeAgo(a.created_at)}</span></span>
                        {a.expires_at && <span className={expired ? 'text-red-400' : ''}>{expired ? 'Expired' : 'Expires'} {formatDate(a.expires_at)}</span>}
                        <span className="flex items-center space-x-1">{Icons.eye('w-3.5 h-3.5')}<span>{readCount}/{totalRecipients} read ({readPct}%)</span></span>
                      </div>
                      <div className="mt-2.5 w-40">
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full transition-all ${readPct === 100 ? 'bg-green-400' : 'bg-primary-400'}`} style={{ width: `${readPct}%` }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button onClick={() => handleTogglePin(a)} title={a.is_pinned ? 'Unpin' : 'Pin to top'} className={`p-2 rounded-lg transition-colors ${a.is_pinned ? 'text-primary-600 bg-primary-50 hover:bg-primary-100' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}>
                        {a.is_pinned ? Icons.pinFilled('w-4 h-4') : Icons.pin('w-4 h-4')}
                      </button>
                      <button onClick={() => handleToggleActive(a)} title={a.is_active ? 'Hide' : 'Show'} className={`p-2 rounded-lg transition-colors ${a.is_active ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-50' : 'text-green-500 bg-green-50 hover:bg-green-100'}`}>
                        {a.is_active ? Icons.eyeOff('w-4 h-4') : Icons.eye('w-4 h-4')}
                      </button>
                      <button onClick={() => { setEditingAnnouncement(a); setShowModal(true); }} title="Edit" className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors">
                        {Icons.pencil('w-4 h-4')}
                      </button>
                      <button onClick={() => setDeletingAnnouncement(a)} title="Delete" className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                        {Icons.trash('w-4 h-4')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            {Icons.megaphone('w-8 h-8 text-gray-300 mx-auto mb-3')}
            <h3 className="font-medium text-gray-900 mb-1">{filter === 'expired' ? 'No inactive announcements' : 'No announcements yet'}</h3>
            <p className="text-sm text-gray-500 mb-4">{filter === 'expired' ? 'All announcements are currently active.' : 'Create your first announcement to keep your team informed.'}</p>
            {filter !== 'expired' && (
              <button onClick={() => { setEditingAnnouncement(null); setShowModal(true); }} className="inline-flex items-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">{Icons.plus('w-4 h-4')}<span>Create Announcement</span></button>
            )}
          </div>
        )}
      </div>

      {announcements.length > 0 && (
        <div className="mt-6 flex items-center justify-center space-x-3 text-xs text-gray-400">
          <span>{activeCount} active</span><span>·</span><span>{expiredCount} inactive</span><span>·</span><span>{announcements.length} total</span>
        </div>
      )}

      {showModal && (
        <AnnouncementModal
          announcement={editingAnnouncement}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingAnnouncement(null); }}
          saving={saving}
        />
      )}
      {deletingAnnouncement && (
        <DeleteModal
          announcement={deletingAnnouncement}
          onConfirm={handleDelete}
          onClose={() => setDeletingAnnouncement(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default AdminAnnouncementsManager;
